<script setup>
// Histórico de disparos por pessoa.
//
// O eixo aqui é o destinatário, não o lote: um disparo para dez contatos vira
// dez linhas do tempo. É a pergunta que se faz na prática — "o Caio recebeu?"
// —, e o lote sozinho não responde, porque a mesma pessoa costuma aparecer em
// disparos diferentes ao longo das semanas.
//
// A busca vai para o servidor, não filtra o que já está na tela: a lista é
// paginada e filtrar só a página atual mostraria "nenhum resultado" para
// alguém que existe na página seguinte.
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import outboundsAPI from 'dashboard/api/outbounds';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import PersonTimeline from './PersonTimeline.vue';
import ConversationPanel from './ConversationPanel.vue';
import ExecutionList from './ExecutionList.vue';
import ExecutionDetail from './ExecutionDetail.vue';
import { whenLabel, fullTimestamp } from '../helpers/outboundHistory';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE = 350;

const { t } = useI18n();
const store = useStore();
const accountId = store.getters.getCurrentAccountId;

const people = ref([]);
const total = ref(0);
const offset = ref(0);
const search = ref('');
const loading = ref(false);
const selected = ref(null);
// A conversa aberta vive aqui, não dentro da tabela nem da linha do tempo:
// ela sobrevive à troca entre lista e detalhe, que é o que faz a conversa
// "abrir na mesma tela" em vez de sumir a cada clique.
const conversation = ref(null);

// Duas leituras do mesmo histórico: por quem recebeu e por execução. A
// execução é a unidade que o operador escolhe quando o mesmo template sai por
// agentes diferentes.
const view = ref('pessoas');
const execution = ref(null);

// O TabBar fala em índice; a tela, no nome da leitura.
const viewTabs = computed(() => [
  { key: 'pessoas', label: t('OUTBOUNDS.HISTORY.VIEW.PESSOAS') },
  { key: 'execucoes', label: t('OUTBOUNDS.HISTORY.VIEW.EXECUCOES') },
]);
const viewIndex = computed(() =>
  viewTabs.value.findIndex(tab => tab.key === view.value)
);

function switchView(next) {
  view.value = next;
  execution.value = null;
  selected.value = null;
}

function openConversation(person) {
  if (!person?.conversa?.id) return;
  conversation.value = {
    id: person.conversa.id,
    title: person.conversa.contact_name || person.nome,
  };
}

let searchTimer = null;

async function load() {
  loading.value = true;
  try {
    const payload = await outboundsAPI.historyPeople(accountId, {
      q: search.value.trim(),
      limit: PAGE_SIZE,
      offset: offset.value,
    });
    people.value = payload?.pessoas || [];
    total.value = payload?.total || 0;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('OUTBOUNDS.HISTORY.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
}

watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    offset.value = 0;
    load();
  }, SEARCH_DEBOUNCE);
});

function goTo(nextOffset) {
  offset.value = Math.max(0, nextOffset);
  load();
}

onMounted(load);
onUnmounted(() => clearTimeout(searchTimer));
</script>

<template>
  <div
    class="grid gap-4 items-start"
    :class="conversation ? 'xl:grid-cols-2' : 'grid-cols-1'"
  >
    <div class="flex flex-col gap-3">
      <TabBar
        :tabs="viewTabs"
        :initial-active-tab="viewIndex"
        @tab-changed="tab => switchView(tab.key)"
      />

      <ExecutionDetail
        v-if="view === 'execucoes' && execution"
        :lote="execution"
        @back="execution = null"
        @open-conversation="openConversation"
      />

      <ExecutionList
        v-else-if="view === 'execucoes'"
        @select="execution = $event"
      />

      <PersonTimeline
        v-else-if="selected"
        :pessoa="selected.pessoa"
        :summary="selected"
        @back="selected = null"
        @open-conversation="openConversation"
      />

      <div v-else class="flex flex-col gap-3">
        <input
          v-model="search"
          type="search"
          class="w-full px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand"
          :placeholder="t('OUTBOUNDS.HISTORY.SEARCH')"
        />

        <p v-if="loading" class="text-sm text-n-slate-10">
          {{ t('OUTBOUNDS.HISTORY.LOADING') }}
        </p>

        <p v-else-if="!people.length" class="text-sm text-n-slate-10">
          {{ t('OUTBOUNDS.HISTORY.EMPTY') }}
        </p>

        <template v-else>
          <div class="overflow-x-auto border rounded-lg border-n-weak">
            <table class="w-full text-sm">
              <thead class="bg-n-alpha-1 text-n-slate-11">
                <tr>
                  <th class="px-3 py-2 font-medium text-left">
                    {{ t('OUTBOUNDS.HISTORY.COLUMNS.PERSON') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-left">
                    {{ t('OUTBOUNDS.HISTORY.COLUMNS.PHONE') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-right">
                    {{ t('OUTBOUNDS.HISTORY.COLUMNS.DISPATCHES') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-left">
                    {{ t('OUTBOUNDS.HISTORY.COLUMNS.OUTCOME') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-left">
                    {{ t('OUTBOUNDS.HISTORY.COLUMNS.LAST') }}
                  </th>
                  <th class="px-3 py-2 font-medium text-left">
                    {{ t('OUTBOUNDS.HISTORY.COLUMNS.CONVERSATION') }}
                  </th>
                  <th class="w-8 px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="person in people"
                  :key="person.pessoa"
                  class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
                  @click="selected = person"
                >
                  <td class="px-3 py-2">
                    <span class="font-medium text-n-slate-12">
                      {{ person.nome || t('OUTBOUNDS.HISTORY.NO_NAME') }}
                    </span>
                    <span
                      v-if="person.email"
                      class="block text-xs text-n-slate-10"
                    >
                      {{ person.email }}
                    </span>
                  </td>
                  <td class="px-3 py-2 tabular-nums text-n-slate-11">
                    {{ person.phone || person.pessoa }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums text-n-slate-12">
                    {{ person.total }}
                  </td>
                  <td class="px-3 py-2">
                    <span class="flex flex-wrap gap-1">
                      <span
                        v-if="person.enviados"
                        class="px-2 py-0.5 text-xs rounded-full bg-n-teal-3 text-n-teal-11 tabular-nums"
                      >
                        {{
                          t(
                            'OUTBOUNDS.HISTORY.SENT_N',
                            { n: person.enviados },
                            person.enviados
                          )
                        }}
                      </span>
                      <span
                        v-if="person.erros"
                        class="px-2 py-0.5 text-xs rounded-full bg-n-ruby-3 text-n-ruby-11 tabular-nums"
                      >
                        {{
                          t(
                            'OUTBOUNDS.HISTORY.FAILED_N',
                            { n: person.erros },
                            person.erros
                          )
                        }}
                      </span>
                      <span
                        v-if="person.pulados"
                        class="px-2 py-0.5 text-xs rounded-full bg-n-amber-3 text-n-amber-11 tabular-nums"
                      >
                        {{
                          t(
                            'OUTBOUNDS.HISTORY.SKIPPED_N',
                            { n: person.pulados },
                            person.pulados
                          )
                        }}
                      </span>
                    </span>
                  </td>
                  <td
                    class="px-3 py-2 text-n-slate-11"
                    :title="fullTimestamp(person.ultimo_envio)"
                  >
                    {{ whenLabel(person.ultimo_envio, t) }}
                    <span class="block text-xs text-n-slate-10">
                      {{ person.ultimo_template }}
                    </span>
                  </td>
                  <td class="px-3 py-2">
                    <button
                      v-if="person.conversa && person.conversa.id"
                      type="button"
                      class="flex items-center gap-1 text-xs text-n-brand hover:underline"
                      @click.stop="openConversation(person)"
                    >
                      <i class="i-lucide-messages-square size-4" />
                      {{ t('OUTBOUNDS.HISTORY.CONVERSATION.OPEN') }}
                    </button>
                    <span v-else class="text-xs text-n-slate-10">
                      {{ t('OUTBOUNDS.HISTORY.CONVERSATION.NONE') }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-n-slate-10">
                    <i class="i-lucide-chevron-right size-4" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="total > PAGE_SIZE"
            class="flex items-center justify-between text-sm text-n-slate-11"
          >
            <span class="tabular-nums">
              {{
                t('OUTBOUNDS.HISTORY.RANGE', {
                  from: offset + 1,
                  to: offset + people.length,
                  total,
                })
              }}
            </span>
            <span class="flex gap-2">
              <button
                type="button"
                class="px-3 py-1 border rounded-lg border-n-weak disabled:opacity-40"
                :disabled="offset === 0 || loading"
                @click="goTo(offset - PAGE_SIZE)"
              >
                {{ t('OUTBOUNDS.HISTORY.PREV') }}
              </button>
              <button
                type="button"
                class="px-3 py-1 border rounded-lg border-n-weak disabled:opacity-40"
                :disabled="offset + people.length >= total || loading"
                @click="goTo(offset + PAGE_SIZE)"
              >
                {{ t('OUTBOUNDS.HISTORY.NEXT') }}
              </button>
            </span>
          </div>
        </template>
      </div>
    </div>

    <ConversationPanel
      v-if="conversation"
      :key="conversation.id"
      :conversation-id="conversation.id"
      :title="conversation.title"
      @close="conversation = null"
    />
  </div>
</template>
